import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * A record preview shown in the sliding <aside>. Pages open a preview when a
 * row is selected (typically driven by a `?id=` query param) and provide a link
 * to the full-size detail route.
 */
export interface DetailPreview {
  /** Short title shown in the aside header (e.g. the record name). */
  title: string;
  /** Optional secondary line under the title. */
  subtitle?: string;
  /** Route to the full-size detail view, e.g. `/accounts/123/details`. */
  fullViewHref?: string;
  /** Preview body. */
  content: React.ReactNode;
  /** Called when the user dismisses the panel (lets the page clear `?id=`). */
  onClose?: () => void;
  /**
   * Aside width. `md` suits denser flows (add-account palette); default `sm`
   * keeps record previews compact.
   */
  size?: 'sm' | 'md';
}

interface DetailPreviewContextValue {
  preview: DetailPreview | null;
  openPreview: (preview: DetailPreview) => void;
  closePreview: () => void;
}

export const DetailPreviewContext = createContext<DetailPreviewContextValue | undefined>(undefined);

export const DetailPreviewProvider = ({ children }: { children: React.ReactNode }) => {
  const [preview, setPreview] = useState<DetailPreview | null>(null);

  const openPreview = useCallback((next: DetailPreview) => setPreview(next), []);
  const closePreview = useCallback(() => setPreview(null), []);

  const value = useMemo(
    () => ({ preview, openPreview, closePreview }),
    [preview, openPreview, closePreview]
  );

  return <DetailPreviewContext.Provider value={value}>{children}</DetailPreviewContext.Provider>;
};

export const useDetailPreview = (): DetailPreviewContextValue => {
  const context = useContext(DetailPreviewContext);
  if (context === undefined) {
    throw new Error('useDetailPreview must be used within a DetailPreviewProvider');
  }
  return context;
};
